import React, { useEffect } from 'react';
import { QrCode, X, Info } from 'lucide-react';
import { useLanguage } from './LanguageContext';
interface Props {
  isOpen: boolean;
  onClose: () => void;
}
/**
 * ScanBadgeSheet — prototype-quality bottom sheet shown when the user taps
 * the Scan / QR button in TreeHome or MatchContainer. Explains the flow and
 * displays a mock QR placeholder. Real camera integration is deferred to
 * the handoff implementation doc.
 */
export function ScanBadgeSheet({
  isOpen,
  onClose
}: Props) {
  const {
    t
  } = useLanguage();
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="absolute inset-0 z-[100] flex flex-col justify-end" data-id="element-277">
      {/* Scrim */}
      <div className="absolute inset-0 bg-black/70 animate-in fade-in duration-200" onClick={onClose} data-id="element-278" />

      {/* Sheet */}
      <div className="relative bg-[#141414] rounded-t-2xl border-t border-[#2A2A2A] px-6 pt-5 flex flex-col max-h-[85vh] overflow-y-auto" style={{
      paddingBottom: 'calc(env(safe-area-inset-bottom) + 32px)',
      animation: 'scanSheetSlideUp 280ms cubic-bezier(0.16, 1, 0.3, 1) both'
    }} data-id="element-279">
        {/* Grabber */}
        <div className="w-10 h-1 bg-[#2A2A2A] rounded-full mx-auto mb-5" data-id="element-280" />

        {/* Header */}
        <div className="flex items-start justify-between gap-3 mb-4" data-id="element-281">
          <div className="flex-1 min-w-0" data-id="element-282">
            <h2 className="text-white text-[17px] font-semibold" data-id="element-283">
              {t('profile.qrTitle')}
            </h2>
            <p className="text-[13px] text-[#A0A0A0] mt-1.5 leading-relaxed" data-id="element-284">
              {t('profile.qrSubtitle')}
            </p>
          </div>
          <button onClick={onClose} aria-label={t('common.close')} className="w-8 h-8 shrink-0 bg-[#1A1A1A] rounded-full flex items-center justify-center text-[#888888] border border-[#2A2A2A] hover:text-white transition-colors" data-id="element-285">
            <X className="w-5 h-5" data-id="element-286" />
          </button>
        </div>

        {/* QR placeholder */}
        <div className="flex items-center justify-center my-2" data-id="element-287">
          <div className="relative w-[220px] h-[220px] bg-white rounded-2xl p-4 flex items-center justify-center" data-id="element-288">
            {/* Mock QR pattern — decorative only */}
            <div aria-hidden className="w-full h-full grid grid-cols-[repeat(21,minmax(0,1fr))] grid-rows-[repeat(21,minmax(0,1fr))] gap-[1px]" data-id="element-289">
              {Array.from({
              length: 21 * 21
            }).map((_, i) => {
              const row = Math.floor(i / 21);
              const col = i % 21;
              const isCornerFinder = row < 7 && col < 7 || row < 7 && col > 13 || row > 13 && col < 7;
              const isFinderRing = isCornerFinder && (row === 0 || row === 6 || col === 0 || col === 6 || (row % 6 < 2 || col % 6 < 2 ? false : true));
              const isFinderCenter = isCornerFinder && row >= 2 && row <= 4 && col >= 2 && col <= 4;
              const isFinderCenter2 = isCornerFinder && (row >= 2 && row <= 4 && col >= 16 && col <= 18 || row >= 16 && row <= 18 && col >= 2 && col <= 4);
              if (isCornerFinder) {
                const inRing = row === 0 || row === 6 || col === 0 || col === 6 || row === 14 && col < 7 || row === 20 && col < 7 || col === 14 && row < 7 || col === 20 && row < 7;
                const inCenter = row >= 2 && row <= 4 && col >= 2 && col <= 4;
                return <div key={i} className={inRing || inCenter ? 'bg-black' : 'bg-white'} data-id="element-290" />;
              }
              // deterministic pseudo-random fill for body
              const on = (row * 7 + col * 13 + (row + col) % 5) % 3 === 0;
              return <div key={i} className={on ? 'bg-black' : 'bg-white'} data-id="element-291" />;
            })}
            </div>
            {/* Centered logo mark */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" data-id="element-292">
              <div className="w-11 h-11 bg-[#C5D82D] rounded-lg flex items-center justify-center border-2 border-white" data-id="element-293">
                <QrCode className="w-5 h-5 text-[#0D0D0D]" strokeWidth={2.5} data-id="element-294" />
              </div>
            </div>
          </div>
        </div>

        {/* Hint row */}
        <div className="flex items-start gap-2 mt-4 px-1" data-id="element-295">
          <Info className="w-4 h-4 text-[#888888] shrink-0 mt-0.5" strokeWidth={2} data-id="element-296" />
          <p className="text-[12px] text-[#888888] leading-relaxed" data-id="element-297">
            {t('profile.qrHint')}
          </p>
        </div>

        {/* Close CTA */}
        <button onClick={onClose} className="mt-6 w-full py-4 rounded-full bg-[#C5D82D] text-[#0D0D0D] text-[14px] font-semibold tracking-wide hover:bg-[#d4e63a] active:scale-[0.98] transition-all" data-id="element-298">
          {t('common.close')}
        </button>

        <style dangerouslySetInnerHTML={{
        __html: `
              @keyframes scanSheetSlideUp {
                from { transform: translateY(100%); opacity: 0; }
                to { transform: none; opacity: 1; }
              }
            `
      }} data-id="element-299" />
      </div>
    </div>;
}